import React, {
  Fragment,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useApolloClient } from "@apollo/client";
import { groupBy, isEmpty, partition, toUpper, upperCase } from "lodash";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { FEATURE_FLAG_NAME } from "common/constants";
import {
  SUBMISSION_CATEGORY,
  SUBMISSION_TYPE_MODULE,
  SUPPORTED_LANGUAGES,
} from "common/constants";
import { buildLink } from "common/routing";
import {
  arrayHasAtLeastOneItem,
  arrayHasAtLeastTwoItems,
} from "common/utils/arrays";
import {
  generateTableLabel,
  isSubmissionTable,
  SUBMISSION_TABLE_TO_CATEGORY,
  TABLE_NAMES,
} from "common/utils/queryBuilder";
import { getSubmissionCategoryTranslationKey } from "common/utils/submissions";
import { isNotNull } from "common/utils/tools";
import { ExtendedApolloClient } from "../../apollo";
import { SavedView } from "../../generated/graphql";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { colors, spacing } from "../../stitches.config";
import {
  SELECTED_LANGUAGE_KEY,
  updateSelectedLanguage,
} from "../../utils/i18n/utils";
import { useOutsideAlerter } from "../../utils/outsideAlerter";
import { track } from "../../utils/tracking";
import { getCurrentURL } from "../../utils/url";
import { usePublicUserAuthModal } from "../Authentication/Public/PublicUserAuthModal";
import { AuthContext } from "../Authorization/AuthContext";
import { FlexColumn } from "../Common/__styles__/Layout";
import { AccountLogo } from "../Common/AccountLogo";
import AITranslationWarningBanner from "../Common/AITranslationWarningBanner";
import { ButtonLink } from "../Common/ButtonLink";
import {
  createSubmissionRedirect,
  hasPropertyAttachment,
} from "../Common/CreateSubmissionButton/utils";
import { Icon } from "../Common/Icons/LucideIcons";
import {
  SkipNavigation,
  SkipNavigationContent,
} from "../Common/SkipNavigation";
import { Body } from "../Common/Typography";
import { DropdownMenu } from "../Inputs";
import { LabelOption } from "../Inputs/__styles__/DropdownMenu";
import { ActionsProps, DropdownOption } from "../Inputs/DropdownMenu";
import { submissionTypeContainsEcDocumentType } from "../Submissions/utils";
import {
  FlexContentWrapper,
  FooterContainer,
  GutterContent,
  GutterContentWrapper,
  HeaderContainer,
  HeaderContent,
  HomeButton,
  MenuParent,
  MenuRow,
  MenuRowSection,
  MenuSection,
  Page,
} from "./__styles__/Layout";
import { HeaderProfile } from "./HeaderProfile";
import { PublicBanner } from "./PublicBanner";
import { accountTitle, useIsMobileWidth, useIsTabletWidth } from "./utils";

const LANGUAGE_MAPPER = {
  en: "English",
  es: "Español",
};

const LanguageButton = ({ inMenu = false }: { inMenu?: boolean }) => {
  const { i18n } = useTranslation();
  const { setValue } = useLocalStorage({ key: SELECTED_LANGUAGE_KEY });
  const client = useApolloClient() as ExtendedApolloClient;
  const account = useContext(AuthContext).account!;
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);

  const supportedLanguages = account.publicPortal.supportedLanguages!;
  if (supportedLanguages!.length === 1) return null;

  const languageActions = supportedLanguages
    .map(language => {
      if (language === i18n.language) return null;
      return {
        label: LANGUAGE_MAPPER[language],
        onClick: () => {
          void updateSelectedLanguage({
            language,
            updateStaticTranslations: i18n.changeLanguage,
            updateDynamicTranslations: client.updateLanguage,
            updateLocalStorage: setValue,
          });

          track("Language changed on public website header", {
            browserLanguage: language,
          });
        },
      };
    })
    .filter(isNotNull);

  const selectedLanguageCopy =
    LANGUAGE_MAPPER[i18n.language as SUPPORTED_LANGUAGES];

  return inMenu ?
      <FlexColumn css={{ gap: spacing.m }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
          onClick={() => setLanguageMenuOpen(!languageMenuOpen)}
        >
          <ButtonLink
            onClick={() => setLanguageMenuOpen(!languageMenuOpen)}
            size="medium"
            leftIconName="globe"
            styleVariant="inverted"
          >
            {selectedLanguageCopy}
          </ButtonLink>
          <Icon
            iconName={languageMenuOpen ? "chevron-up" : "chevron-down"}
            size={16}
            color="contentPrimaryInverted"
          />
        </div>
        {languageMenuOpen &&
          languageActions.map((action, index) => (
            <ButtonLink
              key={index}
              onClick={action.onClick!}
              styleVariant="inverted"
              size="medium"
              style={{ paddingLeft: "24px" }}
            >
              {action.label}
            </ButtonLink>
          ))}
      </FlexColumn>
    : <DropdownMenu
        actions={languageActions}
        customButton={({ onPress }) => (
          <ButtonLink
            onClick={onPress}
            size="small"
            leftIconName="globe"
            rightIconName="chevron-down"
            styleVariant="inverted"
          >
            {selectedLanguageCopy}
          </ButtonLink>
        )}
        position="below-right"
      />;
};

const AccessRecordsButton = ({ inMenu = false }: { inMenu?: boolean }) => {
  const { t } = useTranslation();
  const { account, supportsElevationCertificates } = useContext(AuthContext);
  const [isInMenuDropdownOpen, setIsInMenuDropdownOpen] = useState(false);

  const viewsByTableName: Record<
    string,
    Array<Pick<SavedView, "id" | "name"> | { name: string }>
  > = groupBy(account?.savedViews, savedView =>
    generateTableLabel(
      savedView.query.source,
      account?.customObjectGroups ?? [],
    ),
  );

  if (supportsElevationCertificates) {
    viewsByTableName[TABLE_NAMES.PERMITS] ??= [];

    viewsByTableName[TABLE_NAMES.PERMITS].unshift({
      name: t("button-elevation-certificates"),
    });
  }

  // Ensure consistent ordering
  const sortedViewsByTableName = Object.entries(viewsByTableName).sort((a, b) =>
    a[0].localeCompare(b[0]),
  );

  const actionsByLabel: Array<{ label: string; actions: Array<ActionsProps> }> =
    [];

  for (const [tableName, views] of sortedViewsByTableName) {
    let label = tableName;

    if (isSubmissionTable(tableName)) {
      const category = SUBMISSION_TABLE_TO_CATEGORY[tableName];
      if (category) {
        label = t(
          getSubmissionCategoryTranslationKey({ plural: true })[category],
        );
      }
    }

    actionsByLabel.push({
      label: upperCase(label),
      actions: views.map(view => {
        if ("id" in view) {
          return {
            label: view.name,
            href: buildLink("residentSavedView", { savedViewId: view.id }),
            lineWrap: true,
            variant: inMenu ? "contentDark" : undefined,
          };
        } else {
          return {
            label: view.name,
            href: buildLink("guestElevationCertificates", {
              accountId: account?.id,
            }),
            lineWrap: true,
            variant: inMenu ? "contentDark" : undefined,
          };
        }
      }),
    });
  }

  if (isEmpty(actionsByLabel)) return null;

  if (inMenu) {
    return (
      <>
        <ButtonLink
          styleVariant="inverted"
          size="medium"
          onClick={() => setIsInMenuDropdownOpen(!isInMenuDropdownOpen)}
          rightIconName={isInMenuDropdownOpen ? "chevron-up" : "chevron-down"}
        >
          {t("access-records")}
        </ButtonLink>
        {isInMenuDropdownOpen && (
          <div
            style={{
              marginLeft: "-16px",
              // If there are many views, we need to enable scrolling.
              // The px offset is for header + some extra space on the bottom
              maxHeight: "calc(100vh - 300px)",
              overflowY: "auto",
            }}
          >
            {actionsByLabel.map(({ label, actions }, i) => {
              return (
                <Fragment key={i}>
                  <LabelOption>
                    <Body
                      size="small"
                      type="emphasis"
                      color="contentPrimaryInverted"
                    >
                      {label}
                    </Body>
                  </LabelOption>
                  {actions
                    .filter(action => !action.hidden)
                    .map((action, index) => {
                      return (
                        <DropdownOption action={action} key={index} isGrouped />
                      );
                    })}
                </Fragment>
              );
            })}
          </div>
        )}
      </>
    );
  }

  return (
    <DropdownMenu
      actions={actionsByLabel}
      actionListWidth="360px"
      customButton={({ onPress }) => (
        <ButtonLink
          styleVariant="inverted"
          size="small"
          onClick={onPress}
          rightIconName="chevron-down"
        >
          {t("access-records")}
        </ButtonLink>
      )}
      position="below-left"
    />
  );
};

const SubmitButton = ({ inMenu = false }: { inMenu?: boolean }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const [isInMenuDropdownOpen, setIsInMenuDropdownOpen] = useState(false);
  const { account } = useContext(AuthContext);
  const supportElevationCertificateUploads =
    account?.publicPortal.supportsDocumentUploads;

  const actionsByLabel: Array<{ label: string; actions: Array<ActionsProps> }> =
    [];

  const creatableSubmissionTypes = account?.submissionTypes.filter(
    submissionType => submissionType.currentUserCanCreate,
  );

  const [ecSubmissionTypes, nonECSubmissionTypes] = partition(
    creatableSubmissionTypes,
    submissionType =>
      submissionType.modules.includes(SUBMISSION_TYPE_MODULE.EC_SUBMISSION),
  );

  const ecSubmissionType = ecSubmissionTypes[0];

  const propertyNonECSubmissionTypes = nonECSubmissionTypes.filter(
    submissionType =>
      hasPropertyAttachment(submissionType) &&
      !submissionTypeContainsEcDocumentType({
        submissionType,
        accountDocumentTypes: account?.accountDocumentTypes,
      }),
  );

  if (supportElevationCertificateUploads && ecSubmissionType) {
    actionsByLabel.push({
      label: toUpper(
        t(
          getSubmissionCategoryTranslationKey({ plural: true })[
            ecSubmissionType.category
          ],
        ),
      ),
      actions: [
        {
          label: ecSubmissionType.name,
          onClick: () =>
            history.push(
              buildLink("guestUploadElevationCertificates", {
                accountId: account.id,
              }),
              { prevLocation: getCurrentURL(location) },
            ),
          lineWrap: true,
          variant: inMenu ? "contentDark" : undefined,
        },
      ],
    });
  }

  if (arrayHasAtLeastOneItem(propertyNonECSubmissionTypes)) {
    const groupedExternalSubmissionTypes = groupBy(
      propertyNonECSubmissionTypes,
      "category",
    );
    Object.entries(groupedExternalSubmissionTypes).forEach(
      ([groupLabel, submissionTypes]) => {
        const label = toUpper(
          t(
            getSubmissionCategoryTranslationKey({ plural: true })[
              groupLabel as SUBMISSION_CATEGORY
            ],
          ),
        );
        if (!actionsByLabel.find(action => action.label === label)) {
          actionsByLabel.push({
            label,
            actions: [],
          });
        }
        actionsByLabel
          .find(action => action.label === label)!
          .actions.push(
            ...submissionTypes.map(submissionType => ({
              label: submissionType.name,
              onClick: () =>
                createSubmissionRedirect({
                  history,
                  submissionTypeId: submissionType.id,
                  isGlobal: true,
                }),
              lineWrap: true,
              variant: inMenu ? ("contentDark" as const) : undefined,
            })),
          );
      },
    );
  }

  const workflowDefinitions = account?.workflowDefinitions;

  if (workflowDefinitions?.length) {
    actionsByLabel.push({
      label: toUpper(t("common-workflows")),
      actions: workflowDefinitions.map(workflowDefinition => ({
        label: workflowDefinition.name,
        onClick: () => {
          history.push(
            buildLink("workflowDefinition", {
              workflowDefinitionId: workflowDefinition.id,
            }),
          );
        },
        lineWrap: true,
        variant: inMenu ? ("contentDark" as const) : undefined,
      })),
    });
  }

  if (isEmpty(actionsByLabel)) return null;

  if (inMenu) {
    return (
      <>
        <ButtonLink
          styleVariant="inverted"
          size="medium"
          onClick={() => setIsInMenuDropdownOpen(!isInMenuDropdownOpen)}
          rightIconName={isInMenuDropdownOpen ? "chevron-up" : "chevron-down"}
        >
          {t("submit")}
        </ButtonLink>
        {isInMenuDropdownOpen && (
          <div
            style={{
              marginLeft: "-16px",
              // If there are many views, we need to enable scrolling.
              // The px offset is for header + some extra space on the bottom
              maxHeight: "calc(100vh - 450px)",
              overflowY: "auto",
            }}
          >
            {actionsByLabel.map(({ label, actions }, i) => {
              return (
                <Fragment key={i}>
                  <LabelOption>
                    <Body
                      size="small"
                      type="emphasis"
                      color="contentPrimaryInverted"
                    >
                      {label}
                    </Body>
                  </LabelOption>
                  {actions
                    .filter(action => !action.hidden)
                    .map((action, index) => {
                      return (
                        <DropdownOption action={action} key={index} isGrouped />
                      );
                    })}
                </Fragment>
              );
            })}
          </div>
        )}
      </>
    );
  }

  return (
    <DropdownMenu
      actions={actionsByLabel}
      actionListWidth="360px"
      customButton={({ onPress }) => (
        <ButtonLink
          styleVariant="inverted"
          size="small"
          onClick={onPress}
          rightIconName="chevron-down"
        >
          {t("submit")}
        </ButtonLink>
      )}
      position="below-left"
    />
  );
};

const GetHelpButton = ({
  inMenu = false,
  propertyId,
}: {
  inMenu?: boolean;
  propertyId?: string;
}) => {
  const history = useHistory();
  const { t } = useTranslation();
  const account = useContext(AuthContext).account!;

  const onGetHelpClick = () =>
    history.push(
      buildLink("getHelp", { accountId: account.id }, { propertyId }),
    );

  const style =
    inMenu ? undefined : { minWidth: "unset", whiteSpace: "nowrap" as const };

  return (
    <ButtonLink
      onClick={onGetHelpClick}
      styleVariant="inverted"
      size={inMenu ? "medium" : "small"}
      style={style}
    >
      {t("contact-us")}
    </ButtonLink>
  );
};

const HeaderRowMenu = ({
  propertyId,
  onClickSignIn,
}: {
  propertyId?: string;
  onClickSignIn: () => void;
}) => {
  const { isFeatureEnabled, account } = useContext(AuthContext);

  const showLanguageButton = arrayHasAtLeastTwoItems(
    account!.publicPortal.supportedLanguages,
  );
  const showHeaderProfile = isFeatureEnabled(FEATURE_FLAG_NAME.PUBLIC_USERS);

  return (
    <MenuRow>
      <MenuRowSection>
        <AccessRecordsButton />
        <SubmitButton />
        <GetHelpButton propertyId={propertyId} />
      </MenuRowSection>
      {showLanguageButton || showHeaderProfile ?
        <MenuRowSection>
          <LanguageButton />
          <HeaderProfile onClickSignIn={onClickSignIn} />
        </MenuRowSection>
      : null}
    </MenuRow>
  );
};

const ExpandedMenu = ({
  propertyId,
  onClickSignIn,
}: {
  propertyId?: string;
  onClickSignIn: () => void;
}) => {
  const { account, isFeatureEnabled } = useContext(AuthContext);
  const showLanguageButton = arrayHasAtLeastTwoItems(
    account!.publicPortal.supportedLanguages,
  );
  const showHeaderProfile = isFeatureEnabled(FEATURE_FLAG_NAME.PUBLIC_USERS);
  return (
    <MenuParent style={{ background: account!.publicPortal.color }}>
      <MenuSection>
        <AccessRecordsButton inMenu />
        <SubmitButton inMenu />
        <GetHelpButton inMenu propertyId={propertyId} />
        {showLanguageButton || showHeaderProfile ?
          <MenuSection>
            <LanguageButton inMenu />
            <HeaderProfile inMenu onClickSignIn={onClickSignIn} />
          </MenuSection>
        : null}
      </MenuSection>
    </MenuParent>
  );
};

const MenuButton = ({ onClick }: { onClick: () => void }) => (
  <Icon
    iconName="align-justify"
    onClick={onClick}
    size={16}
    color="contentPrimaryInverted"
    testId="menu-button"
  />
);

interface HeaderProps {
  propertyId?: string;
  openLoginModal?: boolean;
}

export const Header = ({ propertyId, openLoginModal }: HeaderProps) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapperRef = useRef(null);
  const { account, publicUser } = useContext(AuthContext);
  const isMobileScreen = useIsMobileWidth();
  const isTabletScreen = useIsTabletWidth();

  const isSmallScreen = isMobileScreen || isTabletScreen;

  useEffect(() => {
    setMenuOpen(false);
  }, [isSmallScreen]);

  const [showPublicUserLoginModal] = usePublicUserAuthModal();

  useEffect(() => {
    if (openLoginModal && !publicUser) {
      showPublicUserLoginModal();
    }
  }, [openLoginModal, showPublicUserLoginModal, publicUser]);

  const onClickSignIn = () => {
    setMenuOpen(false);
    showPublicUserLoginModal();
  };

  // when someone clicks outside of the mobile menu the menu will disappear
  useOutsideAlerter({
    ref: wrapperRef,
    onOutsideInteraction: () => setMenuOpen(false),
  });

  const subtitle = account!.publicPortal.navBarTitle;

  return (
    <>
      <HeaderContainer
        style={{ background: account!.publicPortal.color }}
        ref={wrapperRef}
        id="header"
      >
        <HeaderContent>
          <HomeButton to={buildLink("guestHome", { accountId: account!.id })}>
            {account!.logoUrl && (
              <AccountLogo account={account!} style={{ marginRight: "8px" }} />
            )}
            <div>
              <Body
                testId="resident-main-header"
                size="large"
                type="emphasis"
                color="contentPrimaryInverted"
              >
                {accountTitle()}
              </Body>
              <Body
                testId="resident-subheader"
                size="small"
                type="regular"
                color="contentPrimaryInverted"
              >
                {subtitle}
              </Body>
            </div>
          </HomeButton>
          {isSmallScreen ?
            <MenuButton onClick={() => setMenuOpen(!menuOpen)} />
          : <HeaderRowMenu
              propertyId={propertyId}
              onClickSignIn={onClickSignIn}
            />
          }
        </HeaderContent>
        {menuOpen && (
          <ExpandedMenu propertyId={propertyId} onClickSignIn={onClickSignIn} />
        )}
      </HeaderContainer>
      <AITranslationWarningBanner />
    </>
  );
};

export const Footer = ({
  color = "contentPlaceholder",
  map = false,
}: {
  color?: keyof typeof colors | "inherit";
  map?: boolean;
}) => {
  const { t } = useTranslation();

  const colorValue = color === "inherit" ? "inherit" : colors[color].toString();

  const css = {
    color: colorValue,
    "& a": {
      color: `${colorValue}`,
    },
  };

  return (
    <FooterContainer id="footer" css={css} map={map}>
      <Body size="default" type="regular" color={"inherit"}>
        <a href="https://www.withforerunner.com/" target="_blank">
          {t("footer-forerunner")}
        </a>{" "}
        |{" "}
        <a
          href="https://www.withforerunner.com/legal/terms-of-services"
          target="_blank"
        >
          {t("footer-terms")}
        </a>
      </Body>
    </FooterContainer>
  );
};

type ColorToken = (typeof colors)[keyof typeof colors];

type LayoutProps = {
  propertyId?: string;
  hasGutters?: boolean;
  children: ReactNode;
  backgroundColor?: ColorToken;
  footerFontColor?: Parameters<typeof Footer>[0]["color"];
};

const Layout = ({
  propertyId,
  children,
  hasGutters = false,
  backgroundColor,
  footerFontColor,
}: LayoutProps) => {
  const childrenDisplay =
    hasGutters ?
      <GutterContentWrapper>
        <GutterContent>{children}</GutterContent>
      </GutterContentWrapper>
    : <FlexContentWrapper>{children}</FlexContentWrapper>;

  return (
    <Page css={{ backgroundColor }}>
      <SkipNavigation />
      <PublicBanner />
      <Header propertyId={propertyId} />
      <SkipNavigationContent>{childrenDisplay}</SkipNavigationContent>
      <Footer color={footerFontColor} />
    </Page>
  );
};

export default Layout;
